import { FC, useCallback } from "react";
import { useDispatch } from "react-redux";

import { ModalPortal, Text } from "@livechat/design-system-react-components";

import ConfirmActionModal from "components/modals/ConfirmActionModal";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { ContactFormActions } from "store/entities/contact-forms/actions";
import { StackingContextLevel } from "styles/stacking-context-level";

interface IProps {
  id: string;
  name: string;
  onClose: () => void;
  isWordpress?: boolean;
}

export const DeleteContactForm: FC<IProps> = ({ id, name, onClose, isWordpress }) => {
  const dispatch = useDispatch();

  const handleDelete = useCallback(() => {
    trackEngagement("Settings: Contact form deleted", EventPlace.Settings, {
      isWordpress: !!isWordpress,
    });
    dispatch(ContactFormActions.delete({ id }));
    onClose();
  }, [id, dispatch, onClose]);

  const content = isWordpress ? (
    <>
      Deleting this contact form will cause the <b>WordPress plugin to stop working correctly</b>. Please disconnect the
      form in the WordPress plugin first to avoid any issues.
    </>
  ) : (
    <>
      You&lsquo;re going to delete <b>{name}</b> contact form. If you added it to your website, it&lsquo;ll stop
      working.
    </>
  );

  return (
    <ModalPortal zIndex={StackingContextLevel.Modal}>
      <ConfirmActionModal
        heading="Delete contact form?"
        onClose={onClose}
        primaryAction={{
          label: "Delete",
          dataTestId: "delete-contact-form-button",
          onClick: handleDelete,
          actionType: "destructive",
        }}
        secondaryAction={{
          label: "Cancel",
          onClick: onClose,
        }}
      >
        <Text>{content}</Text>
      </ConfirmActionModal>
    </ModalPortal>
  );
};
